import { IAppointment } from '@sondermind/data-access/appointments';
import { IProvider } from '@sondermind/data-access/client-portal-referrals';
import { IProviderSearch } from '@sondermind/data-access/public-facing/provider-search';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';

/** HTTP status returned when client already has a session during the requested time slot */
export const CLIENT_SESSION_CONFLICT_ERROR_CODE = 409;
/** User-facing message when backend returns CLIENT_SESSION_CONFLICT_ERROR_CODE */
export const CLIENT_SESSION_CONFLICT_ERROR_MESSAGE = 'Session overlaps with an existing session';

/** HTTP status returned when the client has made too many attempts in a short window */
export const RATE_LIMIT_ERROR_CODE = 429;
/** HTTP status the backend returns for a generic authorization failure on the booking request */
export const AUTHORIZATION_FAILED_ERROR_CODE = 403;
/** HTTP status the backend uses today as a collapsed bucket for validation/business rejections */
export const BAD_REQUEST_ERROR_CODE = 400;

/** Terminal copy for an authorization failure (403, or a 400 scheduling-not-allowed rejection) */
export const AUTHORIZATION_FAILED_ERROR_MESSAGE =
  "Your account can't schedule right now — please review your billing or message your care team.";
/** Neutral honest copy for a 400 whose sub-reason cannot be distinguished */
export const UNDETERMINED_BOOKING_ERROR_MESSAGE =
  "We couldn't schedule that session. Please try again, or message your care team if this keeps happening.";
/** Generic fallback copy for an unmapped/unknown failure */
export const GENERIC_BOOKING_ERROR_MESSAGE = 'Sorry! There was an issue booking that time';

/** Subheader for transient failures where re-picking a time helps (e.g. 409 overlap) */
export const TRANSIENT_REPICK_SUBHEADER = 'Please choose another time.';
/** Subheader for terminal failures — the honest next action lives in the message, not a time-repick */
export const TERMINAL_ERROR_SUBHEADER = '';
/**
 * Lowercase phrase in a 400 `base` message that identifies a scheduling-not-allowed rejection.
 * Matches the real backend copy ("session cannot be scheduled") so unrelated scheduling 400s
 * (e.g. "reschedule", "scheduling window closed") are not mis-routed to the terminal account copy.
 * This string match is a stopgap: the backend exposes no machine-readable reason on this path yet;
 * prefer the machine reason code once one is available.
 */
export const SCHEDULING_NOT_ALLOWED_HINT = 'session cannot be scheduled';

/**
 * Machine-readable error token the backend returns (inside a 409's error body) when the client
 * already has a session with this provider on the requested date. Shared constant so the producer
 * (backend contract) and this consumer reference one source of truth.
 */
export const SESSION_ALREADY_EXISTS_WITH_PROVIDER_ON_DATE_ERROR =
  'SESSION_ALREADY_EXISTS_WITH_PROVIDER_ON_DATE_ERROR';
/** Dedicated user-facing message for the same-day-per-provider 409 (distinct from a slot overlap) */
export const SAME_DAY_PROVIDER_CONFLICT_ERROR_MESSAGE =
  'You already have a session with this provider on that day';
/** Subheader for the same-day-per-provider conflict — the resolving action is picking another day */
export const SAME_DAY_PROVIDER_CONFLICT_SUBHEADER = 'Please choose another day.';

export enum BookingStates {
  INITIAL = 'INITIAL',
  INITIAL_WITHOUT_TIMESLOTS = 'INITIAL_WITHOUT_TIMESLOTS',
  NEW_CLIENT_NO_TIMESLOTS = 'NEW_CLIENT_NO_TIMESLOTS',
  EXISTING_CLIENT_NO_TIMESLOTS = 'EXISTING_CLIENT_NO_TIMESLOTS',
  ALREADY_PROPOSED_TIME = 'ALREADY_PROPOSED_TIME',
  LOADING = 'LOADING',
  CONFIRMED = 'CONFIRMED',
  REQUESTED = 'REQUESTED',
  ADDED_TO_MATCHES = 'ADDED_TO_MATCHES',
  RESCHEDULE = 'RESCHEDULE',
  CANCELLING = 'CANCELLING',
  ERROR = 'ERROR',
  COMPLETING_ACCOUNT = 'COMPLETING_ACCOUNT',
  ADDING_EBCG_PAYMENT = 'ADDING_EBCG_PAYMENT',
  ADDING_PAYMENT_METHOD = 'ADDING_PAYMENT_METHOD',
  CONFIRM_DISCARD_BOOKING = 'CONFIRM_DISCARD_BOOKING'
}

export enum BookingFlow {
  EXISTING_CLIENT = 'EXISTING_CLIENT', // Existing client with referral member - uses direct scheduling API
  NEW_CLIENT = 'NEW_CLIENT' // New client - uses quick checkout API
}

type DirectBookingBaseWithFlow = {
  providerPreview: IProvider | IProviderSearch;
} & (
  | { bookingFlow: BookingFlow.EXISTING_CLIENT; clientReferralMemberId: number; }
  | { bookingFlow: BookingFlow.NEW_CLIENT; clientReferralMemberId?: never; }
);

// Reschedule and Cancel can only be used with EXISTING_CLIENT flow
type DirectBookingBaseExistingClient = {
  providerPreview: IProvider | IProviderSearch;
  bookingFlow: BookingFlow.EXISTING_CLIENT;
  clientReferralMemberId: number;
};

// If bookingState is RESCHEDULE or CANCELLING, must be existing client and appointment is required
// If bookingState is INITIAL_WITHOUT_TIMESLOTS, appointment and slot are optional
// Otherwise, slot is required
export type DirectBookingData =
  | (DirectBookingBaseExistingClient & {
    bookingState: BookingStates.RESCHEDULE | BookingStates.CANCELLING;
    appointment: IAppointment;
    slot?: ISessionRequestPreferences;
  })
  | (DirectBookingBaseWithFlow & {
    bookingState?: Exclude<BookingStates, BookingStates.RESCHEDULE | BookingStates.CANCELLING>;
    appointment?: IAppointment;
    slot: ISessionRequestPreferences;
  })
  | (DirectBookingBaseWithFlow & {
    bookingState: BookingStates.INITIAL_WITHOUT_TIMESLOTS;
    appointment?: IAppointment;
    slot?: ISessionRequestPreferences;
  });

export type DirectBookingResult = {
  bookingState: BookingStates;
  message: string;
  success: boolean;
  redirectPath?: string;
};
